'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

type ViewLayout = 'list' | 'grid-2' | 'grid-3';

interface QuickAction {
  id: string;
  icon: string;
  label: string;
  order: number;
}

interface SlidePanelContextType {
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  viewLayout: ViewLayout;
  setViewLayout: (layout: ViewLayout) => void;
  quickActions: QuickAction[];
  updateQuickActions: (actions: QuickAction[]) => void;
}

const defaultQuickActions: QuickAction[] = [
  { id: 'new-task', icon: 'TaskIcon', label: 'New Task', order: 0 },
  { id: 'log-activity', icon: 'ActivityIcon', label: 'Log Activity', order: 1 },
  { id: 'create-post', icon: 'EditIcon', label: 'Create Post', order: 2 },
  { id: 'create-event', icon: 'CalendarIcon', label: 'Create Event', order: 3 },
  { id: 'meal-plan', icon: 'UtensilsIcon', label: 'New Meal Plan', order: 4 },
  { id: 'breathwork', icon: 'BreatheIcon', label: 'Practice Breathwork', order: 5 },
  { id: 'calorie-tracker', icon: 'CameraIcon', label: 'AI Calorie Tracker', order: 6 },
  { id: 'consultation', icon: 'StethoscopeIcon', label: 'Schedule Consultation', order: 7 },
  { id: 'blood-test', icon: 'TestTubeIcon', label: 'Order Blood Test', order: 8 },
  { id: 'supplements', icon: 'PillIcon', label: 'Order Supplements', order: 9 },
  { id: 'shop', icon: 'ShopIcon', label: 'Shop', order: 10 },
  { id: 'wearable', icon: 'WatchIcon', label: 'Order Wearable', order: 11 },
  { id: 'live', icon: 'VideoIcon', label: 'Go Live', order: 12 },
  { id: 'discourse', icon: 'MicrophoneIcon', label: 'Host Discourse', order: 13 }
];

const SlidePanelContext = createContext<SlidePanelContextType | undefined>(undefined);

export function SlidePanelProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewLayout, setViewLayout] = useState<ViewLayout>(() => {
    // Try to load saved layout from localStorage, default to grid-3
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('quickActionsState');
      if (saved) {
        const { layout } = JSON.parse(saved);
        return layout;
      }
    }
    return 'grid-3';
  });

  const [quickActions, setQuickActions] = useState<QuickAction[]>(() => {
    // Try to load saved state from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('quickActionsState');
      if (saved) {
        const { actions } = JSON.parse(saved);
        return actions;
      }
    }
    return defaultQuickActions;
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('quickActionsState', JSON.stringify({
        actions: quickActions,
        layout: viewLayout
      }));
    }
  }, [quickActions, viewLayout]);

  const openPanel = () => setIsOpen(true);
  const closePanel = () => setIsOpen(false);

  const updateQuickActions = (actions: QuickAction[]) => {
    setQuickActions(actions);
  };

  return (
    <SlidePanelContext.Provider 
      value={{ 
        isOpen, 
        openPanel, 
        closePanel,
        viewLayout,
        setViewLayout,
        quickActions,
        updateQuickActions
      }}
    >
      {children}
    </SlidePanelContext.Provider>
  );
}

export function useSlidePanel() {
  const context = useContext(SlidePanelContext);
  if (context === undefined) {
    throw new Error('useSlidePanel must be used within a SlidePanelProvider');
  }
  return context;
}
